<template>
  <!-- Docked presentation (≥3xl, dockable route, user-expanded): a sticky
       flex sibling of the main content — content reflows to its width. -->
  <div
    v-if="docked"
    class="sticky top-0 block h-screen min-w-0 flex-none self-start"
    :style="{ width: 'var(--sidepanel-width, 0px)' }"
  >
    <div
      v-tooltip="'Drag to resize — double-click to reset'"
      class="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize bg-transparent transition-colors hover:bg-purple-300"
      :class="{ 'bg-purple-300': sidepanelDock.isDockResizing.value }"
      @mousedown="startDockResize"
      @dblclick="sidepanelDock.dockWidthPx.value = null"
    ></div>
    <section
      id="sidepanel-heading"
      class="flex size-full overscroll-none"
      aria-label="Sidepanel"
    >
      <div
        class="flex size-full flex-col overflow-hidden border-y border-l border-gray-200 bg-white"
      >
        <div class="-mt-px flex h-13 items-stretch border-b border-gray-200">
          <PanelTabButton
            v-for="def in visibleModes"
            :key="def.id"
            v-tooltip.bottom="modeTitle(def)"
            :data-tour="`sidepanel-${def.id}`"
            :aria-label="modeTitle(def)"
            :active="activeModeId === def.id"
            @select="openMode(def.id)"
          >
            <component
              :is="def.tabIcon"
              v-if="def.tabIcon"
              :active="activeModeId === def.id"
              :size="18"
              icon-size-class="size-4.5"
            />
            <Icon v-else-if="def.icon" :name="def.icon" class="size-4.5" />
          </PanelTabButton>
        </div>
        <component
          :is="activeDef.headerActions"
          v-if="activeDef.headerActions"
          variant="docked"
        />
        <SidepanelBody :mode-id="activeModeId" />
      </div>
    </section>
  </div>

  <!-- A collapsed dock exposes the edge stack; opening a mode restores the
       dock when space permits. Smaller viewports use the overlay. -->
  <template v-else>
    <section
      class="fixed right-0 bottom-0 z-40 flex max-w-full overscroll-none"
      :class="{ 'pointer-events-none opacity-0': isSuspended }"
      :inert="isSuspended || undefined"
      :style="{ top: 'var(--page-frame-bottom, 0px)' }"
      aria-labelledby="sidepanel-heading"
    >
      <Transition
        :enter-active-class="
          panelSwitching ? '' : 'transition ease-in-out duration-500'
        "
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        :leave-active-class="
          panelSwitching ? '' : 'transition ease-in-out duration-500'
        "
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-show="isOpen"
          ref="panelRef"
          class="sidepanel-overlay-shadow relative w-screen"
          :style="{ maxWidth: panelMaxWidth }"
        >
          <div
            class="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize bg-transparent transition-colors hover:bg-purple-300"
            @mousedown="startResize"
          ></div>
          <div
            class="flex h-full flex-col overflow-hidden border-y border-l border-gray-200 bg-white"
          >
            <div
              id="sidepanel-heading"
              class="flex flex-row items-center justify-between border-b border-gray-200 px-3 py-2"
            >
              <div class="flex items-center gap-2">
                <FyrAiLogo
                  v-if="headerIcon === null"
                  :size="16"
                  class="text-purple-600"
                />
                <Icon
                  v-else
                  :name="headerIcon"
                  class="size-4 text-purple-600"
                />
                <h2 class="text-sm font-semibold text-burgundy-900">
                  {{ modeTitle(activeDef) }}
                </h2>
              </div>
              <div class="flex items-center gap-1">
                <component
                  :is="activeDef.headerActions"
                  v-if="activeDef.headerActions"
                  variant="overlay"
                />
                <button
                  v-tooltip="'Close panel'"
                  class="rounded-md p-1 text-gray-400 hover:text-gray-600"
                  @click="dismissOverlay"
                >
                  <span class="sr-only">Close panel</span>
                  <Icon name="times" class="size-4" />
                </button>
              </div>
            </div>
            <SidepanelBody :mode-id="activeModeId" />
          </div>
        </div>
      </Transition>
    </section>

    <!-- Right-edge toggle buttons: one stack that follows the open panel -->
    <div
      v-if="!isResizing && !isSuspended && visibleModes.length > 0"
      class="fixed z-40 transition-[right] duration-500 ease-in-out"
      :style="{
        top: '16rem',
        right: isOpen ? panelMaxWidth : '0px',
      }"
    >
      <div
        class="flex flex-col rounded-l-md border border-gray-200 bg-white [&>button]:border-b [&>button]:border-gray-200 [&>button:first-child]:rounded-tl-md [&>button:last-child]:rounded-bl-md [&>button:last-child]:border-b-0"
        :class="isOpen ? 'sidepanel-overlay-shadow border-r-0' : 'shadow-md'"
      >
        <button
          v-for="def in visibleModes"
          :key="def.id"
          v-tooltip="modeTitle(def)"
          :data-tour="`sidepanel-${def.id}`"
          type="button"
          :aria-label="modeTitle(def)"
          :aria-pressed="isOpen && activeModeId === def.id"
          class="px-3 py-2 transition-colors"
          :class="overlayTabClasses(def.id)"
          @click="toggleOverlayMode(def.id)"
        >
          <component
            :is="def.tabIcon"
            v-if="def.tabIcon"
            :active="isOpen && activeModeId === def.id"
            :size="20"
            icon-size-class="size-5"
          />
          <Icon v-else-if="def.icon" :name="def.icon" class="size-5" />
        </button>
      </div>
    </div>
  </template>

  <template v-for="def in visibleModes" :key="`${def.id}-${resetGeneration}`">
    <component :is="def.persistent" v-if="def.persistent" />
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { DateTime } from "luxon";
import { useRoute } from "vue-router";
import Icon from "@/components/Core/Icon.vue";
import FyrAiLogo from "@/components/Core/FyrAiLogo.vue";
import PanelTabButton from "@/components/Core/PanelTabButton.vue";
import SidepanelBody from "@/components/Sidepanel/SidepanelBody.vue";
import "@/components/Sidepanel/modes";
import {
  claimAiAutoOpen,
  panelSwitching,
  sidepanelOpenRequests,
  useSidepanel,
  type SidepanelModeDef,
} from "@/composables/useSidepanel";
import {
  MIN_DOCK_WIDTH_PX,
  clampedDockWidth,
  persistDockWidth,
  useSidepanelDock,
  writeSidepanelWidth,
} from "@/composables/useSidepanelDock";
import { useDragLifecycle, usePanelResize } from "@/composables/usePanelResize";
import { useBp } from "@/utils/breakpoints";
import { useAuthStore } from "@/store/auth";
import { useProfileQuery, type Profile } from "@/services/user";

const {
  selectedMode,
  isOpen,
  availableModes,
  modeDefs,
  openMode,
  close,
  panelWidth,
  panelMaxWidth,
  isResizing,
  isSuspended,
  resetGeneration,
} = useSidepanel();

const sidepanelDock = useSidepanelDock();
const { panelRef, startResize } = usePanelResize(panelWidth, isResizing);

const docked = sidepanelDock.isDocked;

const visibleModes = availableModes;
const activeModeId = computed(
  () =>
    visibleModes.value.find((mode) => mode.id === selectedMode.value)?.id ??
    visibleModes.value[0]?.id ??
    selectedMode.value,
);
const activeDef = computed(
  () => modeDefs.value.get(activeModeId.value) ?? modeDefs.value.get("ai")!,
);
function modeTitle(def: SidepanelModeDef): string {
  return typeof def.title === "function" ? def.title() : def.title;
}

function overlayTabClasses(modeId: string): string {
  if (!isOpen.value) {
    return "bg-white text-gray-600 hover:bg-gray-50";
  }
  if (activeModeId.value === modeId) {
    return "sidepanel-active-tab relative z-10 -mr-px bg-white text-purple-700";
  }
  return "sidepanel-inactive-tab bg-gray-50 text-gray-500 hover:bg-white hover:text-gray-700";
}

const headerIcon = computed(() =>
  activeDef.value.headerIcon !== undefined
    ? activeDef.value.headerIcon
    : activeDef.value.icon,
);

// The docked presentation is always visible: it renders selectedMode and
// ignores isOpen, but keeps the flag truthful so `isModeOpen` consumers see
// reality — and clears it when undocking so the overlay doesn't pop open.
watch(
  docked,
  (isDocked) => {
    isOpen.value = isDocked;
  },
  { immediate: true },
);

// Below the dock breakpoint the panel would start hidden behind its edge
// tabs, so the AI chat goes unnoticed: open it once per login until
// someone closes the chat there. Respects a collapsed dock (same user choice);
// skipped under lg, where the overlay would cover most of the page, and for
// accounts younger than AI_AUTO_OPEN_MIN_ACCOUNT_AGE_DAYS.
// The dismissal is per browser and never cleared, on purpose: the nudge only
// makes the chat discoverable, so once anyone here has closed it (at any
// width), reopening it per user or per login would be nagging.
const AI_OVERLAY_DISMISSED_KEY = "sidepanel-ai-overlay-dismissed";
const bp = useBp();
const authStore = useAuthStore();

function isAiOverlayDismissed(): boolean {
  try {
    return localStorage.getItem(AI_OVERLAY_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

// A brand-new user hasn't found their way around yet, and the chat sliding
// over their first pages reads as intrusive rather than helpful.
const AI_AUTO_OPEN_MIN_ACCOUNT_AGE_DAYS = 4;
const profileQuery = useProfileQuery();

function isNewAccount(profile: Profile): boolean {
  if (!profile.dateJoined) return false;
  return (
    DateTime.fromISO(profile.dateJoined) >
    DateTime.now().minus({ days: AI_AUTO_OPEN_MIN_ACCOUNT_AGE_DAYS })
  );
}

// Views open their own modes from onMounted, after this setup, and the account
// age needs the profile, which can land later still. Any mode opened in
// between, by a view or the user, has already taken the panel this visit,
// even if it has been closed or undocked since.
const openRequestsAtSetup = sidepanelOpenRequests();

// Decided once per mount: a later refetch must not slide the chat in mid-visit,
// so a failed first fetch decides too (against opening). A slow or retried
// first fetch can land after the user has moved on to another page, which is
// just as mid-visit.
const route = useRoute();
onMounted(() => {
  let decided = false;
  const pathAtMount = route.path;
  watch(
    () => [profileQuery.data, profileQuery.isError] as const,
    ([profile, failed]) => {
      if (decided || (!profile && !failed)) return;
      decided = true;
      if (!profile) return;
      if (
        sidepanelDock.isExpanded.value &&
        !bp.smaller.lg &&
        !isOpen.value &&
        sidepanelOpenRequests() === openRequestsAtSetup &&
        route.path === pathAtMount &&
        !isAiOverlayDismissed() &&
        !isNewAccount(profile) &&
        claimAiAutoOpen(authStore.authRevision)
      ) {
        openMode("ai");
      }
    },
    { immediate: true },
  );
});

// Only the AI chat counts: closing e.g. the dashboard Widgets panel says
// nothing about the chat.
function dismissOverlay(): void {
  if (activeModeId.value === "ai") {
    claimAiAutoOpen(authStore.authRevision);
    try {
      localStorage.setItem(AI_OVERLAY_DISMISSED_KEY, "true");
    } catch {
      // Blocked or full storage must not stop the close; the claim above
      // still keeps the chat shut for this login, at least until a reload.
    }
  }
  close();
}

function toggleOverlayMode(modeId: string): void {
  if (isOpen.value && activeModeId.value === modeId) {
    dismissOverlay();
  } else {
    openMode(modeId);
  }
}

// Covers both edge-tab clicks and modes opened by feature actions.
watch([isOpen, sidepanelDock.canDock], ([open, canDock]) => {
  if (open && canDock) sidepanelDock.isExpanded.value = true;
});

// The AI mode stays registered whatever the flag says: a non-entitled org
// keeps the tab and gets the pitch inside it (AiModeContent renders the
// paywall), so the panel is always there and only its content changes.
// Nothing to close or fall back to — which is why the old flag watcher that
// called setSidepanelModeEnabled("ai", visible) is gone.

// One synchronous write moves the panel edge and the shell bars in the same
// paint; via the reactive binding the panel would lag the bars by a frame.
// rAF-throttled because each frame reflows the main content (charts, grids).
function applyDockWidth(clientX: number): void {
  const max = Math.round(window.innerWidth * 0.35);
  // The panel's right edge sits at the viewport's right edge.
  const next = Math.min(
    max,
    Math.max(MIN_DOCK_WIDTH_PX, window.innerWidth - clientX),
  );
  // Not a hardcoded true: undocking mid-drag would reserve a gutter for a
  // panel that is no longer rendered.
  writeSidepanelWidth(sidepanelDock.isDocked.value, clampedDockWidth(next));
  sidepanelDock.dockWidthPx.value = next;
}

const { start: startDockResize, stop: stopDockResize } = useDragLifecycle({
  isResizing: sidepanelDock.isDockResizing,
  throttle: "frame",
  onMove: applyDockWidth,
  onTeardown() {
    // dockWidth may clamp differently than the last frame (35vw excludes the
    // scrollbar), and the width watcher skips persisting mid-drag.
    writeSidepanelWidth(
      sidepanelDock.isDocked.value,
      sidepanelDock.dockWidth.value,
    );
    persistDockWidth();
  },
});

// Undocking mid-drag removes the element being dragged; end the drag so the
// body styles and the document-level listeners do not outlive it.
watch(docked, (isDocked) => {
  if (!isDocked) stopDockResize();
});
</script>

<style scoped>
.sidepanel-overlay-shadow {
  box-shadow: -0.75rem 0 1.5rem -0.75rem rgb(30 2 2 / 18%);
}

.sidepanel-active-tab {
  box-shadow: -0.5rem 0 0.75rem -0.125rem rgb(191 154 255 / 55%);
}

.sidepanel-inactive-tab {
  box-shadow: inset -0.375rem 0 0.5rem -0.5rem rgb(30 2 2 / 18%);
}
</style>
